/**
 * Character sets for email validation - extracted for maintainability
 * These can be extended if we need to support additional character ranges
 */
const alphanumericAndExtendedLatin = 'a-zA-Z0-9\\u00C0-\\u024F\\u1E00-\\u1EFF'
const specialCharsLocalPart = "!#$%&'*+/=?^_`{|}~-"
const specialCharsDomain = '-'

/**
 * Global regex validator for email addresses with international character support
 *
 * REGEX PATTERN BREAKDOWN:
 * ^[alphanumericAndExtendedLatin + specialCharsLocalPart]+  -- Local part (before @)
 *   - a-zA-Z0-9: Standard ASCII letters and numbers
 *   - \u00C0-\u024F: Latin Extended-A and Latin Extended-B (includes ä, ö, ü, é, ñ, ß, etc.)
 *   - \u1E00-\u1EFF: Latin Extended Additional (various accented Latin letters)
 *   - !#$%&'*+/=?^_`{|}~-: Special characters allowed by RFC 5322
 *
 * (?:\.[alphanumericAndExtendedLatin + specialCharsLocalPart]+)*  -- Optional dot-separated segments
 *   - Prevents leading/trailing dots (e.g., .test@ or test.@)
 *   - Prevents consecutive dots (e.g., test..user@)
 *   - Allows valid formats like first.last@example.com
 *
 * @  -- Required @ separator
 *
 * [alphanumericAndExtendedLatin + specialCharsDomain]+  -- Domain name part
 *   - Same character sets as local part but more restrictive
 *   - Only allows hyphen (-) as special character (no other special chars in domain)
 *
 * (?:\.[alphanumericAndExtendedLatin + specialCharsDomain]+)+$  -- Required TLD and optional subdomains
 *   - Must have at least one dot followed by domain part (e.g., .com, .de)
 *   - Supports subdomains (e.g., mail.example.com)
 *   - Prevents trailing dots and consecutive dots in domain
 *
 * WHY THIS APPROACH:
 * - RFC 5322 only supports ASCII, but real-world usage requires international characters
 * - RFC 6530-6532 (Email Address Internationalization) allows full UTF-8, but that's too permissive
 * - This regex is a practical middle ground: supports European languages while maintaining security
 * - Prevents homograph attacks by limiting to Latin-based scripts only
 *
 * SUPPORTED EXAMPLES:
 * ✓ müller@example.de (German umlauts)
 * ✓ françois@société.fr (French accents)
 * ✓ josé@españa.es (Spanish ñ and accents)
 * ✓ łukasz@przykład.pl (Polish special characters)
 * ✓ info@ärztehaus-münchen.de (umlauts in domain)
 *
 * REJECTED EXAMPLES:
 * ✗ .test@example.com (leading dot)
 * ✗ test..user@example.com (consecutive dots)
 * ✗ тест@example.com (Cyrillic - security risk)
 * ✗ 测试@example.com (Chinese - not Latin-based)
 * ✗ test@example (missing TLD)
 */
const localPart = `[${alphanumericAndExtendedLatin}${specialCharsLocalPart}]+`
const localPartWithDots = `${localPart}(?:\\.${localPart})*`
const domainPart = `[${alphanumericAndExtendedLatin}${specialCharsDomain}]+`
const domainWithSubdomains = `${domainPart}(?:\\.${domainPart})+`

export const emailRegex = new RegExp(`^${localPartWithDots}@${domainWithSubdomains}$`)

/**
 * Global regex validator for URLs
 */
export const urlRegex = /^(\w+):\/\/(.*)/

/**
 * Matches a URL that starts with a scheme, such as `https:` or `mailto:`
 */
export const urlSchemeRegex = /^[a-z][a-z0-9+.-]*:/i

/**
 * Validator for rich-text editor link values.
 * Accepts explicit-protocol URLs, supported web URLs without protocols, supported protocol-relative URLs,
 * and root-relative app URLs.
 */
export const richTextEditorLinkValueRegex = /^(?:[a-z][a-z0-9+.-]*:|(?:www\.|ftp\.)|\/\/(?:www\.|ftp\.)|\/(?!\/))/i

/**
 * Global regex validator for URLs within text
 */
export const textUrlRegex =
  /(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gi

/**
 * Global regex validator for plain text URLs within html
 */
export const plainHtmlUrlRegex =
  /(?<!\bsrc=["'])((?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gi

/**
 * Global regex validator for Server Message Block URLs within text
 */
export const smbUrlRegex =
  /smb:\/\/.(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gi

/**
 * Global regex for URLs within text including any protocol
 */
export const textUrlRegexExtended =
  /(?:[a-z]*:\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:;,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:;,.]*\)|[A-Z0-9+&@#/%=~_|;$])/gi

/**
 * Global regex for GUID
 */
export const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
export const guidRegexString = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'

/**
 * Guid regex for mentions: Fetching any id
 */
export const guidRegexForMentions = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/

/**
 * Global regex to validate float numbers
 */
export const floatRegex = /^[0-9]+((.|,)[0-9]{1,2})?$/

/**
 * Global regex for mention elements, independent of the attribute order
 */
export const mentionElementRegex = /<span(?=[^>]*\sclass="mention")[^>]*>[^>]*<\/span>/g

/**
 * Regex for the data-id attribute value of a mention element
 */
export const mentionDataIdRegex = /\sdata-id="([^"]*)"/

/**
 * Regex for mention ids that comments store as `~[id]` placeholders
 */
export const persistedMentionIdRegex =
  /^(?:(?:userId|teamId|agentId):[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|agentType:personal-agent|project|workspace|task)$/

/**
 * Global regex for File Tags
 */
export const fileTagRegex = /~\[fileId:([\s\S]*?)(?=])]/g

/**
 * Global regex for mentionedUser
 */
export const userMentionRegex = /~\[userId:([\s\S]*?)(?=])]/g

/**
 * Global regex for mentioned agents.
 */
export const agentMentionRegex = /~\[(?:agentId:([\s\S]*?)(?=])|agentType:personal-agent)]/g

/**
 * Global regex for mentionedTeam
 */
export const teamMentionRegex = /~\[teamId:([\s\S]*?)(?=])]/g

/**
 * File icon Component Regex
 */
export const fileIconComponentRegex = /<aw-file-icon[^>]*[^>]*>[^~]*?<\/aw-file-icon>/g

/**
 * Emoji regex validations
 */
export const emojiRegex =
  /[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01\uddff]|\ud83c[\ude01-\ude02]|[\ud83c[\ude32\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|[\ud83c[\ude50\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff]/g

/**
 * Device category regex
 */
export const mobileRegex =
  /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series([46])0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/gim
export const tabletRegex =
  /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/gim

/**
 * Opening HTML tags regex (<p>, <a>, <h1>, ecc...)
 * It do not match closing and self-closing tags
 */
export const openingHtmlTagsRegex = /<([a-z]+)(?![^>]*\/>)[^>]*>/

export const fileTagElRegex = /<span class="file-tag[^>]*>[\s\S]*<\/span>/g
